import { useFetcher, useLoaderData, useNavigate, type LoaderFunctionArgs } from 'react-router';
import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Textarea,
  toast,
} from '@repo-manager/ui';
import { kbEntryType, kbSeverity } from '@repo-manager/shared';

import { getApi } from '../lib/api';
import { getSupabase } from '../lib/supabase';

interface EntryFull {
  id: string;
  slug: string;
  title: string;
  type: 'pattern' | 'standard' | 'reference_impl' | 'lesson';
  severity: 'standard' | 'recommended' | 'optional';
  summary: string;
  content: string;
  rationale: string | null;
  code_example: string | null;
  status: 'draft' | 'active' | 'deprecated';
  version: number;
}

export async function editKbLoader({ params }: LoaderFunctionArgs): Promise<{ entry: EntryFull }> {
  if (!params.id) throw new Response('Not Found', { status: 404 });
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('kb_entries')
    .select('id, slug, title, type, severity, summary, content, rationale, code_example, status, version')
    .eq('id', params.id)
    .single();
  if (error || !data) throw new Response('Entry not found', { status: 404 });
  return { entry: data as EntryFull };
}

export async function editKbAction({ params, request }: LoaderFunctionArgs) {
  if (!params.id) return null;
  const fd = await request.formData();
  const op = fd.get('op');
  const supabase = getSupabase();
  if (op === 'set-status') {
    const raw = String(fd.get('status'));
    if (raw !== 'draft' && raw !== 'active' && raw !== 'deprecated') {
      return { ok: false, error: `invalid_status:${raw}` };
    }
    const { error } = await supabase.from('kb_entries').update({ status: raw }).eq('id', params.id);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }
  return null;
}

export function EditKbPage() {
  const { entry } = useLoaderData() as { entry: EntryFull };
  const navigate = useNavigate();
  const fetcher = useFetcher();
  const [draft, setDraft] = useState({
    title: entry.title,
    type: entry.type,
    severity: entry.severity,
    summary: entry.summary,
    content: entry.content,
    rationale: entry.rationale ?? '',
    code_example: entry.code_example ?? '',
    changelog: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const r = fetcher.data;
    if (r && typeof r === 'object' && 'ok' in r) {
      if ((r as { ok: boolean }).ok) toast.success('Status updated');
      else toast.error('Update failed', { description: (r as { error?: string }).error });
    }
  }, [fetcher.data]);

  async function save() {
    setSaving(true);
    const supabase = getSupabase();
    const contentChanged =
      draft.content !== entry.content ||
      draft.title !== entry.title ||
      draft.summary !== entry.summary ||
      draft.rationale !== (entry.rationale ?? '');
    const { error } = await supabase
      .from('kb_entries')
      .update({
        title: draft.title,
        type: draft.type,
        severity: draft.severity,
        summary: draft.summary,
        content: draft.content,
        rationale: draft.rationale || null,
        code_example: draft.code_example || null,
        changelog: draft.changelog || null,
        version: entry.version + 1,
      })
      .eq('id', entry.id);
    if (error) {
      setSaving(false);
      toast.error('Save failed', { description: error.message });
      return;
    }
    if (contentChanged) {
      try {
        await getApi().kbEmbed([entry.slug]);
      } catch (e) {
        toast.warning('Saved; re-embed pending', {
          description: e instanceof Error ? e.message : String(e),
        });
      }
    }
    setSaving(false);
    toast.success('Entry saved');
    navigate(`/knowledge/${entry.slug}`);
  }

  function setStatus(status: 'draft' | 'active' | 'deprecated') {
    const fd = new FormData();
    fd.set('op', 'set-status');
    fd.set('status', status);
    fetcher.submit(fd, { method: 'post' });
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Edit entry</h1>
          <div className="mt-1 flex items-center gap-2">
            <code className="rounded bg-muted px-2 py-0.5 text-xs">{entry.slug}</code>
            <StatusBadge status={entry.status} />
            <span className="text-sm text-muted-foreground">v{entry.version}</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">Status…</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setStatus('active')}>Mark active</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setStatus('draft')}>Mark draft</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setStatus('deprecated')}>Deprecate</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fields</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-3">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={draft.type} onValueChange={(v) => setDraft((d) => ({ ...d, type: v as EntryFull['type'] }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {kbEntryType.options.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={draft.severity} onValueChange={(v) => setDraft((d) => ({ ...d, severity: v as EntryFull['severity'] }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {kbSeverity.options.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="summary">Summary</Label>
            <Textarea
              id="summary"
              rows={2}
              value={draft.summary}
              onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Textarea
              id="content"
              rows={10}
              value={draft.content}
              onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rationale">Rationale</Label>
            <Textarea
              id="rationale"
              rows={4}
              value={draft.rationale}
              onChange={(e) => setDraft((d) => ({ ...d, rationale: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="code_example">Code example</Label>
            <Textarea
              id="code_example"
              rows={8}
              className="font-mono text-xs"
              value={draft.code_example}
              onChange={(e) => setDraft((d) => ({ ...d, code_example: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="changelog">Changelog (this revision)</Label>
            <Input
              id="changelog"
              value={draft.changelog}
              onChange={(e) => setDraft((d) => ({ ...d, changelog: e.target.value }))}
              placeholder="Why this change?"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save (bumps version, re-embeds)'}
            </Button>
            <Button variant="outline" onClick={() => navigate(`/knowledge/${entry.slug}`)}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
